import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, DocumentTextIcon, UserGroupIcon, CogIcon } from '@heroicons/react/24/outline';

const Privacy: React.FC = () => {
  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      content: [
        'Account information (name, email, company details)',
        'Invoice data and documents uploaded to our platform',
        'Usage data and analytics to improve our service',
        'Technical information (IP address, browser type, device information)'
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      content: [
        'Process and analyze invoices using AI technology',
        'Provide customer support and technical assistance',
        'Improve our AI algorithms and service quality',
        'Send important updates about your account and service'
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security & Protection',
      content: [
        'Bank-grade encryption (AES-256) for all data',
        'SOC 2 Type II compliance and regular audits',
        'Secure data centers with 24/7 monitoring',
        'Multi-factor authentication and access controls'
      ]
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing & Disclosure',
      content: [
        'We do not sell your personal information to third parties',
        'Limited sharing with trusted service providers under strict agreements',
        'Legal compliance when required by law or regulation',
        'Your consent for any other sharing scenarios'
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <ShieldCheckIcon className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            <p className="text-xl text-gray-600">
              Your privacy and data security are our top priorities. Learn how we protect and handle your information.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: January 15, 2025</p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              AICaFriend ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your data. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              AI-powered invoice processing platform. By using AICaFriend, you agree to the practices described in this policy.
            </p>
          </motion.div>

          {/* Key Sections */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="bg-gray-50 rounded-xl p-8"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h3>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Data Retention */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 bg-indigo-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Retention</h3>
            <p className="text-gray-600 mb-4">
              We retain your data only as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul className="space-y-2 text-gray-600">
              <li>• Account data: Retained while your account is active</li>
              <li>• Invoice data: Processed and stored securely for accounting compliance</li>
              <li>• Backup data: Maintained for disaster recovery purposes</li>
              <li>• Deleted data: Permanently removed within 30 days of deletion request</li>
            </ul>
          </motion.div>

          {/* Your Rights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 bg-green-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h3>
            <p className="text-gray-600 mb-4">
              You have the following rights regarding your personal data:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Access & Portability</h4>
                <p className="text-gray-600 text-sm">Request a copy of your personal data in a portable format</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Correction</h4>
                <p className="text-gray-600 text-sm">Update or correct inaccurate personal information</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Deletion</h4>
                <p className="text-gray-600 text-sm">Request deletion of your personal data (right to be forgotten)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Restriction</h4>
                <p className="text-gray-600 text-sm">Limit how we process your personal information</p>
              </div>
            </div>
          </motion.div>

          {/* Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Cookies & Tracking</h3>
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies</h4>
                  <p className="text-gray-600 text-sm">Required for basic functionality and security</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h4>
                  <p className="text-gray-600 text-sm">Help us understand how you use our platform</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Preference Cookies</h4>
                  <p className="text-gray-600 text-sm">Remember your settings and preferences</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* International Transfers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-12"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">International Data Transfers</h3>
            <p className="text-gray-600">
              Your data is primarily stored and processed in secure data centers within India. If we need to transfer 
              data internationally, we ensure adequate protection through approved transfer mechanisms and contractual 
              safeguards that meet international standards.
            </p>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white"
          >
            <h3 className="text-xl font-semibold mb-4">Contact Us About Privacy</h3>
            <p className="mb-4">
              If you have questions about this Privacy Policy or how we handle your data, please contact us:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Email</h4>
                <p className="text-indigo-100">privacy@aicafriend.com</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Data Protection Officer</h4>
                <p className="text-indigo-100">dpo@aicafriend.com</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Address</h4>
                <p className="text-indigo-100">AICaFriend Technologies<br />Bangalore, Karnataka, India</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Response Time</h4>
                <p className="text-indigo-100">Within 72 hours for urgent privacy matters</p>
              </div>
            </div>
          </motion.div>

          {/* Updates */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="mt-12 text-center"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Policy Updates</h3>
            <p className="text-gray-600">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
              We will notify you of significant changes via email or through our platform. Your continued use of 
              AICaFriend after such changes constitutes acceptance of the updated policy.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;